import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Download, TrendingUp, TrendingDown,
  Minus, AlertTriangle, RefreshCw, Lock, TrendingUp as ProjIcon,
  ShieldCheck, AlertTriangle as WarnIcon, XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtEur = (n, compact = false) => {
  if (n == null) return "—";
  if (compact && Math.abs(n) >= 1000)
    return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(n);
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
};
const fmtInt = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES").format(n));
const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);
const fmtRatio = (n) => (n == null ? "—" : `${n.toFixed(2)}x`);

// ── Block config — NO projField anywhere (table = real only) ──────────────────
const BLOCKS = [
  {
    key: "volumen", label: "VOLUMEN",
    color: "bg-blue-600", textColor: "text-blue-700", bgColor: "bg-blue-50",
    rows: [
      { key: "orders", label: "Pedidos", fmt: fmtInt, conditional: null },
      { key: "revenue", label: "Revenue", fmt: fmtEur, conditional: null },
      { key: "aov", label: "AOV", fmt: fmtEur, conditional: null },
    ],
  },
  {
    key: "variable", label: "RENTABILIDAD VARIABLE",
    color: "bg-violet-600", textColor: "text-violet-700", bgColor: "bg-violet-50",
    rows: [
      { key: "cogs", label: "COGS", fmt: fmtEur, conditional: null },
      { key: "gross_margin", label: "Margen Bruto €", fmt: fmtEur, conditional: null },
      { key: "gross_margin_pct", label: "MB %", fmt: fmtPct, conditional: null },
      { key: "shipping_margin", label: "Envío Margen €", fmt: fmtEur, conditional: null },
      { key: "ads_spend", label: "Gasto Ads", fmt: (v, c) => v == null ? "—" : `−${fmtEur(v, c)}`, conditional: null, isAds: true },
      { key: "contribution_margin", label: "Contribución € (post-ads)", fmt: fmtEur, conditional: null },
      { key: "contribution_margin_pct", label: "Contribución %", fmt: fmtPct, conditional: null },
    ],
  },
  {
    key: "estructura", label: "ESTRUCTURA Y RESULTADO",
    color: "bg-emerald-600", textColor: "text-emerald-700", bgColor: "bg-emerald-50",
    rows: [
      { key: "fixed_costs", label: "Costes Fijos", fmt: fmtEur, conditional: null },
      { key: "fixed_cost_per_order", label: "Coste Fijo/Pedido", fmt: fmtEur, conditional: null },
      { key: "operating_profit", label: "Bfº Operativo €", fmt: fmtEur, conditional: "profit" },
      { key: "operating_margin_pct", label: "MO %", fmt: fmtPct, conditional: "margin" },
      { key: "break_even", label: "Break-even €", fmt: fmtEur, conditional: null },
      { key: "cushion_ratio", label: "Colchón", fmt: fmtRatio, conditional: "cushion" },
      { key: "safety_margin", label: "Margen Seguridad €", fmt: fmtEur, conditional: "safety" },
    ],
  },
  {
    key: "calidad", label: "CALIDAD DE NEGOCIO",
    color: "bg-amber-600", textColor: "text-amber-700", bgColor: "bg-amber-50",
    rows: [
      { key: "new_orders_pct", label: "% Pedidos Nuevos", fmt: fmtPct, conditional: null },
      { key: "returning_orders_pct", label: "% Recurrentes", fmt: fmtPct, conditional: null },
      { key: "cancellation_pct", label: "% Cancelaciones", fmt: fmtPct, conditional: null },
      { key: "return_pct", label: "% Devoluciones", fmt: fmtPct, conditional: null },
    ],
  },
];

const SHORT_MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

// ── Conditional formatting ────────────────────────────────────────────────────
function condClass(type, value) {
  if (value == null) return "";
  switch (type) {
    case "margin": return value > 8 ? "text-emerald-700 font-bold" : value > 3 ? "text-amber-600 font-bold" : "text-red-600 font-bold";
    case "cushion": return value > 1.3 ? "text-emerald-700 font-bold" : value > 1.1 ? "text-amber-600 font-bold" : "text-red-600 font-bold";
    case "safety": return value > 0 ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold";
    case "profit": return value > 0 ? "text-emerald-700 font-semibold" : value < 0 ? "text-red-600 font-semibold" : "text-slate-500";
    default: return "";
  }
}

// ── Pace badge ────────────────────────────────────────────────────────────────
// pct = (current_period_revenue / prev_same_period_revenue) * 100
// e.g. 128 means current month days 1-N is 28% more than same days last month
function PaceBadge({ pct, vsLabel }) {
  if (!pct) return null;
  const delta = pct - 100;
  const high = delta > 10, low = delta < -5;
  const icon = high ? <TrendingUp className="w-2.5 h-2.5" /> : low ? <TrendingDown className="w-2.5 h-2.5" /> : <Minus className="w-2.5 h-2.5" />;
  const cls = high ? "bg-emerald-50 text-emerald-700 border-emerald-200" : low ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200";
  const label = `${delta >= 0 ? "+" : ""}${delta.toFixed(0)}% vs ${vsLabel || "mes ant."}`;
  return (
    <TooltipProvider>
      <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded border cursor-default ${cls}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-0.5">{icon} {label}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-slate-900 text-white border-slate-700 text-xs max-w-[220px]">
            Revenue acumulado mismo periodo vs mes anterior
          </TooltipContent>
        </Tooltip>
      </span>
    </TooltipProvider>
  );
}

// ── Simple cell (real only, no projField subtexts) ────────────────────────────
function Cell({ val, row, isCur }) {
  if (val == null) return <span className="text-slate-200 text-xs">—</span>;
  const cls = condClass(row.conditional, val) || (row.isAds ? "text-red-500" : "text-slate-800");
  return (
    <span className={`tabular-nums text-xs ${cls} ${isCur ? "font-black" : ""}`}>
      {row.fmt(val, true)}
    </span>
  );
}

// ── Export CSV ────────────────────────────────────────────────────────────────
function exportCSV(year, months, totals_real, totals_projected) {
  const mlabels = months.map((m) => m.month_label);
  const header = ["Bloque", "Métrica", ...mlabels, "TOTAL REAL", "TOTAL PROY"];
  const rows = [header];
  BLOCKS.forEach((block) => {
    block.rows.forEach((row) => {
      const cells = months.map((m) => {
        if (m.is_future) return "";
        const v = m[row.key];
        return v == null ? "" : String(v);
      });
      rows.push([block.label, row.label, ...cells,
        totals_real?.[row.key] ?? "", totals_projected?.[row.key] ?? ""]);
    });
  });
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `resumen-financiero-${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── KPI card for the "Mes Actual" analysis block ──────────────────────────────
function KpiCard({ label, value, sub, cls }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-base font-black tabular-nums ${cls || "text-slate-800"}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

// ── Status config ─────────────────────────────────────────────────────────────
const PNL_STATUS = {
  profitable:     { border: "border-emerald-400", bg: "bg-emerald-50",  accent: "text-emerald-700", badgeBg: "bg-emerald-500 text-white", label: "RENTABLE",  Icon: ShieldCheck },
  near_breakeven: { border: "border-amber-400",   bg: "bg-amber-50",    accent: "text-amber-700",   badgeBg: "bg-amber-500 text-white",   label: "CERCA BE",  Icon: WarnIcon    },
  losing:         { border: "border-red-400",      bg: "bg-red-50",      accent: "text-red-700",     badgeBg: "bg-red-500 text-white",     label: "SIN BE",    Icon: XCircle     },
};

// ── P&L row ───────────────────────────────────────────────────────────────────
function PnlRow({ label, value, highlight, separator, tooltip }) {
  const content = (
    <div className={`flex justify-between items-baseline py-1 ${separator ? "border-t border-dashed border-slate-200 mt-1 pt-2" : ""}`}>
      <span className="text-xs text-slate-500 leading-tight">
        {label}
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="ml-1 text-[10px] text-slate-300 cursor-default underline decoration-dotted">(?)</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-900 text-white border-slate-700 text-xs max-w-[240px] whitespace-pre-line">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </span>
      <span className={`text-sm font-bold tabular-nums ${highlight || "text-slate-800"}`}>{value}</span>
    </div>
  );
  return tooltip ? <TooltipProvider>{content}</TooltipProvider> : content;
}

// ── "Mes Actual" P&L Operativo block ─────────────────────────────────────────
function CurrentMonthBlock({ month }) {
  if (!month) return null;
  const p = month.projection || {};
  const hasProj = !!p.run_rate_factor;
  const pace = month.pace_vs_last_month_pct;
  const cfg = PNL_STATUS[p.financial_status] || PNL_STATUS.losing;
  const StatusIcon = cfg.Icon;

  // Derive previous month short label for the pace badge (e.g. "Feb" when current = "2026-03")
  const prevMonthLabel = (() => {
    if (!month.month) return "mes ant.";
    const [, mm] = month.month.split("-");
    const prevIdx = (parseInt(mm, 10) - 2 + 12) % 12; // 0-based index of prev month
    return SHORT_MONTHS[prevIdx];
  })();

  const fmtE = (v) => v == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  const fmtP = (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`;
  const fmtN = (v) => v == null ? "—" : new Intl.NumberFormat("es-ES").format(Math.round(v));
  const posColor = (v) => v == null ? "" : v > 0 ? "text-emerald-700" : v < 0 ? "text-red-600" : "text-slate-500";

  return (
    <div className="space-y-4" data-testid="current-month-analysis">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-sm font-black text-slate-800 uppercase tracking-wide">
          {month.month_label} — P&amp;L Operativo
        </h2>
        {hasProj && (
          <span className={`inline-flex items-center gap-1 text-xs font-black px-2 py-0.5 rounded-full ${cfg.badgeBg}`} data-testid="pnl-status-badge">
            <StatusIcon className="w-3 h-3" /> {cfg.label}
          </span>
        )}
        {pace != null && pace > 0 && <PaceBadge pct={pace} vsLabel={prevMonthLabel} />}
        {p.days_elapsed != null && (
          <span className="text-xs text-slate-400">
            Día {p.days_elapsed} de {p.days_in_month}
            {p.run_rate_factor && <span className="ml-1 text-slate-300">· ×{Number(p.run_rate_factor).toFixed(2)} run-rate</span>}
          </span>
        )}
      </div>

      {/* ── Insight automático — justo debajo del título ── */}
      {hasProj && p.insight_message && (
        <div className={`rounded-xl border ${cfg.border} ${cfg.bg} px-4 py-3 flex items-start gap-3`} data-testid="pnl-insight">
          <div className="flex-shrink-0 mt-0.5">
            <StatusIcon className={`w-4 h-4 ${cfg.accent}`} />
          </div>
          <p className={`text-sm font-medium leading-relaxed ${cfg.accent}`}>{p.insight_message}</p>
        </div>
      )}

      {/* ── 2-Column P&L ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

        {/* Col 1: REAL HOY */}
        <div className={`rounded-xl border-l-4 ${cfg.border} border border-slate-200 bg-white p-4`} data-testid="real-section">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Real hoy</span>
            <span className="text-[9px] text-slate-300 ml-auto">FC prorr. · Envío estimado</span>
          </div>

          <PnlRow label="Revenue" value={fmtE(month.revenue)} highlight="text-slate-900 font-black" />
          <PnlRow label="− COGS" value={month.cogs ? fmtE(-month.cogs) : "—"} highlight="text-slate-500" />
          <PnlRow label="= Gross Margin" value={fmtE(month.gross_margin)} separator highlight={posColor(month.gross_margin)} />
          <PnlRow
            label="+ Shipping Margin (est.)"
            value={fmtE(p.real_shipping_margin_prorated ?? month.shipping_margin)}
            highlight={posColor(p.real_shipping_margin_prorated ?? month.shipping_margin)}
            tooltip={`Cobros envío reales: ${fmtE(p.real_shipping_rev)}\n− Coste est. (${p.avg_shipping_cost_per_order ? fmtE(p.avg_shipping_cost_per_order) : '—'}/ped × ${fmtN(month.orders)} ped.): ${fmtE(p.real_shipping_cost_prorated ? -p.real_shipping_cost_prorated : null)}\n= Margen envío: ${fmtE(p.real_shipping_margin_prorated)}\nPromedio de ${p.avg_shipping_months_used ?? 0} meses cerrados`}
          />
          {p.avg_shipping_cost_per_order > 0 ? (
            <p className="text-[9px] text-slate-400 italic pl-1 -mt-0.5 mb-1">
              ~{fmtE(p.avg_shipping_cost_per_order)}/ped · prom. {p.avg_shipping_months_used}m
            </p>
          ) : (
            <p className="text-[9px] text-amber-500 italic pl-1 -mt-0.5 mb-1">
              Sin historial de costes de envío
            </p>
          )}
          <PnlRow label="− Ad Spend" value={month.ads_spend ? fmtE(-month.ads_spend) : "—"} highlight="text-red-500" />
          <PnlRow label="= Contribution" value={fmtE(p.real_contribution_prorated ?? month.contribution_margin)} separator highlight={`font-black ${posColor(p.real_contribution_prorated ?? month.contribution_margin)}`} />
          <PnlRow label="− Fixed Costs (prorr.)" value={p.real_fixed_costs_prorated != null ? fmtE(-p.real_fixed_costs_prorated) : "—"} highlight="text-slate-500" />
          <PnlRow label="= Bfº Operativo" value={fmtE(p.real_op_profit)} separator highlight={`font-black text-base ${posColor(p.real_op_profit)}`} />
          <PnlRow label="Margen Operativo" value={fmtP(p.real_op_margin_pct)} highlight={posColor(p.real_op_profit)} />

          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Pedidos</p>
              <p className="text-sm font-bold text-slate-700">{fmtN(month.orders)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">AOV</p>
              <p className="text-sm font-bold text-slate-700">{fmtE(month.aov)}</p>
            </div>
          </div>
        </div>

        {/* Col 2: PREVISIÓN FIN DE MES — same waterfall as REAL HOY */}
        {hasProj ? (
          <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4`} data-testid="projection-section">
            <div className="flex items-center gap-2 mb-3">
              <ProjIcon className={`w-3.5 h-3.5 ${cfg.accent}`} />
              <span className={`text-[10px] font-black uppercase tracking-widest ${cfg.accent}`}>Previsión fin de mes</span>
            </div>

            <PnlRow label="Revenue proy." value={fmtE(p.projected_revenue)} highlight="text-slate-900 font-black" />
            <PnlRow label="− COGS proy." value={p.projected_cogs ? fmtE(-p.projected_cogs) : "—"} highlight="text-slate-500" />
            <PnlRow label="= Gross Margin proy." value={fmtE(p.projected_gross_margin)} separator highlight={posColor(p.projected_gross_margin)} />
            <PnlRow
              label="+ Shipping Margin proy."
              value={fmtE(p.projected_shipping_margin)}
              highlight={posColor(p.projected_shipping_margin)}
              tooltip={`Cobros envío proy.: ${fmtE(p.projected_shipping_rev)}\n− Coste est. (${p.avg_shipping_cost_per_order ? fmtE(p.avg_shipping_cost_per_order) : '—'}/ped × ${fmtN(p.projected_orders)} ped.): ${fmtE(p.projected_shipping_cost ? -p.projected_shipping_cost : null)}\n= Margen envío: ${fmtE(p.projected_shipping_margin)}\nPromedio de ${p.avg_shipping_months_used ?? 0} meses cerrados`}
            />
            {p.avg_shipping_cost_per_order > 0 ? (
              <p className="text-[9px] text-slate-400 italic pl-1 -mt-0.5 mb-1">
                ~{fmtE(p.avg_shipping_cost_per_order)}/ped × {fmtN(p.projected_orders)} ped.
              </p>
            ) : null}
            <PnlRow label="− Ad Spend proy." value={p.projected_ad_spend ? fmtE(-p.projected_ad_spend) : "—"} highlight="text-red-500" />
            <PnlRow label="= Contribution proy." value={fmtE(p.projected_contribution)} separator highlight={`font-black ${posColor(p.projected_contribution)}`} />
            <PnlRow label="− Fixed Costs (mes)" value={p.projected_fixed_costs != null ? fmtE(-p.projected_fixed_costs) : "—"} highlight="text-slate-500" />
            <PnlRow label="= Bfº Operativo proy." value={fmtE(p.projected_operating_profit)} separator highlight={`font-black text-base ${posColor(p.projected_operating_profit)}`} />
            <PnlRow label="Margen Operativo" value={fmtP(p.projected_operating_margin_pct)} highlight={posColor(p.projected_operating_profit)} />

            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Pedidos proy.</p>
                <p className="text-sm font-bold text-slate-700">{fmtN(p.projected_orders)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">AOV</p>
                <p className="text-sm font-bold text-slate-700">{fmtE(month.aov)}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-center opacity-50">
            <p className="text-xs text-slate-400 italic">Sin datos suficientes para proyectar.</p>
          </div>
        )}
      </div>

      {/* ── Decision Metrics (4 cols) ── */}
      {hasProj && p.break_even_revenue != null && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid="decision-metrics">
          <div className="px-4 py-3 text-center">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Bfº Op. Previsión</p>
            <p className={`text-base font-black tabular-nums ${posColor(p.projected_operating_profit)}`} data-testid="projected-op-profit">
              {fmtE(p.projected_operating_profit)}
            </p>
            <p className="text-[10px] text-slate-400">{fmtP(p.projected_operating_margin_pct)}</p>
          </div>
          <div className="px-4 py-3 text-center border-l border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Break-even revenue</p>
            <p className="text-base font-black tabular-nums text-slate-800" data-testid="break-even-revenue">{fmtE(p.break_even_revenue)}</p>
          </div>
          <div className="px-4 py-3 text-center border-l border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Revenue gap</p>
            <p className={`text-base font-black tabular-nums ${(p.revenue_gap || 0) > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="revenue-gap">
              {(p.revenue_gap || 0) > 0 ? `+${fmtE(p.revenue_gap)}` : fmtE(Math.abs(p.revenue_gap || 0))}
            </p>
            <p className="text-[10px] text-slate-400">{(p.revenue_gap || 0) > 0 ? "necesario para BE" : "sobre break-even"}</p>
          </div>
          <div className="px-4 py-3 text-center border-l border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Margin of Safety</p>
            <p className={`text-base font-black tabular-nums ${(p.margin_of_safety_pct || 0) > 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="margin-of-safety">
              {fmtP(p.margin_of_safety_pct)}
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FinancialYearPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const orgId = localStorage.getItem("active_organization_id");
      const params = { year };
      if (orgId) params.organization_id = orgId;
      const res = await axios.get(`${API}/api/finance/year-summary`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setData(res.data);
    } catch {
      setError("Error cargando resumen anual");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleSyncAds = async () => {
    setSyncing(true);
    try {
      const orgId = localStorage.getItem("active_organization_id");
      const params = { year };
      if (orgId) params.organization_id = orgId;
      await axios.post(`${API}/api/finance/sync-ads-spend`, null, {
        params, headers: getHeaders(), withCredentials: true,
      });
      toast.success(`Gasto publicitario sincronizado para ${year}`);
      load();
    } catch {
      toast.error("Error sincronizando gasto ads.");
    } finally {
      setSyncing(false);
    }
  };

  const goToMonth = (month) => {
    if (!month || month.is_future) return;
    const [y, m] = month.month.split("-");
    const daysInMonth = new Date(parseInt(y), parseInt(m), 0).getDate();
    navigate(`/dashboard?start=${y}-${m}-01&end=${y}-${m}-${String(daysInMonth).padStart(2, "0")}`);
  };

  const months = data?.months || [];
  const totals_real = data?.totals_real;
  const totals_proj = data?.totals_projected;
  const currentMonth = months.find((m) => m.is_current_month);
  const closedMonthsCount = totals_real?.months_included ?? 0;

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1700px] mx-auto px-4 py-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">
              Resumen Financiero Anual
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Total Real = solo meses cerrados · Mes actual = bloque operativo independiente
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg px-1 py-1">
              <button onClick={() => setYear((y) => y - 1)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-slate-600 transition-colors" data-testid="prev-year-btn">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-sm font-bold text-slate-800 min-w-[44px] text-center tabular-nums" data-testid="current-year-label">{year}</span>
              <button onClick={() => setYear((y) => y + 1)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-slate-600 transition-colors" data-testid="next-year-btn">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {data && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => exportCSV(year, months, totals_real, totals_proj)} data-testid="export-csv-btn">
                <Download className="w-3.5 h-3.5" /> Exportar CSV
              </Button>
            )}

            <Button
              variant="outline" size="sm"
              className={`h-8 text-xs gap-1.5 ${(data?.ads_unsynced_months > 0) ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}`}
              onClick={handleSyncAds} disabled={syncing}
              data-testid="sync-ads-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sincronizar Ads"}
            </Button>
          </div>
        </div>

        {/* ── Unsynced warning ── */}
        {data?.ads_unsynced_months > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="ads-unsynced-warning">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-amber-700 text-xs">
              <strong>{data.ads_unsynced_months} mes{data.ads_unsynced_months > 1 ? "es" : ""}</strong> sin gasto Google Ads + Meta sincronizado.{" "}
              <button onClick={handleSyncAds} className="underline font-semibold hover:text-amber-900">Sincronizar Ads</button>
            </span>
          </div>
        )}

        {loading && <div className="h-64 flex items-center justify-center text-sm text-slate-400">Cargando {year}...</div>}
        {error && <div className="h-32 flex items-center justify-center text-sm text-red-500">{error}</div>}

        {!loading && !error && months.length > 0 && (
          <>
            {/* ── ANNUAL TABLE ── */}
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="year-summary-table">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b-2 border-slate-200">
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase sticky left-0 bg-slate-50 min-w-[155px] z-10">
                      Métrica
                    </th>
                    {months.map((m, i) => {
                      const isCur = m.is_current_month;
                      const isFut = m.is_future;
                      return (
                        <th key={m.month} className={`px-2 py-2.5 text-center min-w-[72px] ${isCur ? "bg-amber-50" : isFut ? "opacity-30" : ""}`} data-testid={`month-header-${m.month}`}>
                          <button
                            onClick={() => goToMonth(m)}
                            disabled={isFut}
                            className={`flex flex-col items-center gap-0.5 w-full ${isFut ? "cursor-default" : "hover:text-red-600 cursor-pointer transition-colors"}`}
                          >
                            <span className={`text-[10px] font-bold uppercase ${isCur ? "text-amber-700" : "text-slate-600"}`}>{SHORT_MONTHS[i]}</span>
                            {isCur && <span className="text-[8px] px-1 py-0 rounded-sm bg-amber-200 text-amber-800 font-semibold">EN CURSO</span>}
                          </button>
                        </th>
                      );
                    })}

                    {/* TOTAL REAL */}
                    <th className="px-2 py-2.5 text-center min-w-[82px] bg-slate-900 border-l-2 border-slate-700">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-black text-white uppercase">Total Real</span>
                        {closedMonthsCount > 0 && (
                          <span className="text-[8px] text-slate-400">{closedMonthsCount} meses cerrados</span>
                        )}
                      </div>
                    </th>

                    {/* TOTAL PROY */}
                    <th className="px-2 py-2.5 text-center min-w-[82px] bg-amber-800 border-l border-amber-700">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-black text-amber-100 uppercase">Total Proy.</span>
                        <span className="text-[8px] text-amber-300">cerrados + mes actual</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {BLOCKS.map((block) => (
                    <Fragment key={block.key}>
                      {/* Block header */}
                      <tr className={`${block.bgColor} border-t-2 border-b border-slate-200`}>
                        <td colSpan={months.length + 3} className={`px-3 py-1.5 sticky left-0 ${block.bgColor}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-sm ${block.color} inline-block`} />
                            <span className={`text-[10px] font-black uppercase tracking-widest ${block.textColor}`}>{block.label}</span>
                          </div>
                        </td>
                      </tr>

                      {/* Metric rows */}
                      {block.rows.map((row, rowIdx) => (
                        <tr
                          key={`${block.key}-${row.key}`}
                          className={`border-b border-slate-50 ${rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50/30"} hover:bg-slate-50/70 transition-colors`}
                          data-testid={`metric-row-${row.key}`}
                        >
                          <td className={`px-3 py-2 sticky left-0 ${rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50/30"} z-10`}>
                            <span className="text-[11px] text-slate-600 font-medium whitespace-nowrap">{row.label}</span>
                          </td>

                          {months.map((m) => {
                            const isCur = m.is_current_month;
                            const isFut = m.is_future;
                            return (
                              <td key={m.month} className={`px-2 py-2 text-right ${isCur ? "bg-amber-50/60" : isFut ? "opacity-25" : ""}`} data-testid={`cell-${m.month}-${row.key}`}>
                                {isFut
                                  ? <span className="text-slate-200 text-xs">—</span>
                                  : (
                                    <div className="flex flex-col items-end gap-0.5">
                                      <Cell val={m[row.key]} row={row} isCur={isCur} />
                                      {isCur && row.isAds && !m.ads_synced && (m[row.key] || 0) === 0 && (
                                        <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />
                                      )}
                                    </div>
                                  )}
                              </td>
                            );
                          })}

                          {/* TOTAL REAL cell */}
                          <td className="px-2 py-2 text-right bg-slate-900/5 border-l-2 border-slate-200">
                            {totals_real ? (
                              <span className={`tabular-nums text-xs font-black ${condClass(row.conditional, totals_real[row.key]) || "text-slate-800"}`}>
                                {row.fmt(totals_real[row.key], true)}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>

                          {/* TOTAL PROY cell */}
                          <td className="px-2 py-2 text-right bg-amber-50/40 border-l border-amber-200">
                            {totals_proj ? (
                              <span className={`tabular-nums text-xs font-semibold ${condClass(row.conditional, totals_proj[row.key]) || "text-amber-800"}`}>
                                {row.fmt(totals_proj[row.key], true)}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── MES ACTUAL: ANÁLISIS OPERATIVO ── */}
            {currentMonth && (
              <div className="border border-amber-200 bg-white rounded-xl p-5 shadow-sm">
                <CurrentMonthBlock month={currentMonth} />
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
